type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };

function normalizeHex(hex: string): string {
  const value = hex.replace("#", "");
  return value.length === 3
    ? value
        .split("")
        .map((c) => c + c)
        .join("")
    : value;
}

function hexToRgb(hex: string): Rgb {
  const full = normalizeHex(hex);
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) return { h: 0, s: 0, l };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;

  return { h: h * 60, s, l };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }

  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hn = h / 360;

  return {
    r: hue2rgb(p, q, hn + 1 / 3) * 255,
    g: hue2rgb(p, q, hn) * 255,
    b: hue2rgb(p, q, hn - 1 / 3) * 255,
  };
}

/**
 * Converts a `#rgb`/`#rrggbb` branding color into an `rgba()` string at an
 * arbitrary alpha — the shared utility so opacity variants (note callout
 * backgrounds/borders, subtle tints, etc.) are derived from the tenant's
 * actual color instead of a second hardcoded hex per use site.
 */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Brightens a color just enough to stay legible against dark surfaces, by
 * raising its HSL lightness to `minLightness` while preserving hue/
 * saturation — a no-op if it's already lighter than that. Used to derive
 * dark-mode variants for branding colors used in a foreground/icon role
 * (text, icons, pins), where an arbitrary tenant hex might otherwise be too
 * dark to read once the surface behind it goes dark.
 */
export function ensureMinLightness(hex: string, minLightness: number): string {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  if (hsl.l >= minLightness) return hex;
  return rgbToHex(hslToRgb({ ...hsl, l: minLightness }));
}

/**
 * The inverse of `ensureMinLightness`: darkens a color down to at most
 * `maxLightness`, preserving hue/saturation — a no-op if it's already
 * darker. Used for branding colors used in an opaque light-background role
 * (e.g. a pastel tint filling a button), which need to become a dark tint
 * in dark mode rather than staying a near-white patch.
 */
export function ensureMaxLightness(hex: string, maxLightness: number): string {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  if (hsl.l <= maxLightness) return hex;
  return rgbToHex(hslToRgb({ ...hsl, l: maxLightness }));
}
